import type { ApiError, ApiErrorCode } from '@pibex/shared';

/** Thrown by `api.*` on any non-2xx response. `code` comes from the `ApiError` body. */
export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(code: ApiErrorCode, status: number, message?: string) {
    super(message ?? code);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
  }
}

async function readErrorBody(res: Response): Promise<Partial<ApiError> | null> {
  try {
    return (await res.json()) as Partial<ApiError>;
  } catch {
    return null;
  }
}

async function parseError(res: Response): Promise<ApiRequestError> {
  const body = await readErrorBody(res);
  const code: ApiErrorCode = body?.error ?? 'internal';
  return new ApiRequestError(code, res.status, body?.message);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  };
  if (body !== undefined) {
    init.headers = { ...init.headers, 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Minimal JSON client. The session cookie is httpOnly and sent by the browser automatically. */
export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>('GET', path);
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('POST', path, body);
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>('PATCH', path, body);
  },
  delete(path: string): Promise<undefined> {
    return request<undefined>('DELETE', path);
  },
};

export function isApiError(err: unknown, code?: ApiErrorCode): err is ApiRequestError {
  return err instanceof ApiRequestError && (code === undefined || err.code === code);
}
